/**
 * RigidBody2D Component
 *
 * Physics body that enables 2D dynamics simulation.
 * Attach to a GameObject to make it participate in physics.
 */

import {
  component,
  displayName,
  icon,
  color,
  category,
  allowMultiple,
  requireComponent,
  field,
  customEditor,
} from "../attributes";
import { EngineComponent } from "../scripting/EngineComponent";
import type { Vec2Like } from "../scripting/IEngineContext";

@component("core.RigidBody2D")
@customEditor("RigidBody2DEditor")
@displayName("Rigidbody 2D")
@icon("Box")
@color("#e17055")
@category("Physics 2D")
@allowMultiple(false)
@requireComponent(["core.Transform"])
export class RigidBody2D extends EngineComponent {
  /** Body type: 0 = Dynamic, 1 = Kinematic, 2 = Static */
  @field({ type: "i32", label: "Body Type", tooltip: "0 = Dynamic, 1 = Kinematic, 2 = Static" })
  bodyType: number = 0;

  /** Mass in kg (auto-computed from colliders if 0) */
  @field({ type: "f32", label: "Mass", min: 0, tooltip: "0 = auto from colliders" })
  mass: number = 0;

  /** Linear drag coefficient */
  @field({ type: "f32", label: "Linear Drag", min: 0, step: 0.01 })
  linearDamping: number = 0;

  /** Angular drag coefficient */
  @field({ type: "f32", label: "Angular Drag", min: 0, step: 0.01 })
  angularDamping: number = 0.05;

  /** Gravity multiplier */
  @field({ type: "f32", label: "Gravity Scale", step: 0.1 })
  gravityScale: number = 1;

  // Constraints
  @field({ type: "bool", label: "Freeze X", group: "Constraints" })
  freezePositionX: boolean = false;

  @field({ type: "bool", label: "Freeze Y", group: "Constraints" })
  freezePositionY: boolean = false;

  @field({ type: "bool", label: "Freeze Rotation", group: "Constraints" })
  freezeRotation: boolean = false;

  // Advanced
  /** Interpolation: 0 = None, 1 = Interpolate, 2 = Extrapolate */
  @field({ type: "i32", label: "Interpolation", hidden: true })
  interpolation: number = 0;

  /** Collision detection: 0 = Discrete, 1 = Continuous */
  @field({ type: "i32", label: "Collision Detection", hidden: true })
  collisionDetection: number = 0;

  /** Allow sleep for performance */
  @field({ type: "bool", label: "Allow Sleep" })
  allowSleep: boolean = true;

  /** Linear velocity threshold for sleeping (0 = engine default) */
  @field({ type: "f32", label: "Sleep Linear Threshold", min: 0, step: 0.01, hidden: true })
  sleepLinearThreshold: number = 0;

  /** Angular velocity threshold for sleeping (0 = engine default) */
  @field({ type: "f32", label: "Sleep Angular Threshold", min: 0, step: 0.01, hidden: true })
  sleepAngularThreshold: number = 0;

  /** Dominance priority for collisions (-127 to 127). Higher dominance wins. */
  @field({ type: "i32", label: "Dominance", min: -127, max: 127, hidden: true })
  dominance: number = 0;

  // ──────────────────────────────────────────────────────────────────────────
  // Runtime API (Unity-style). Not @field → not serialized. Methods proxy to the
  // physics subsystem for the bound entity via the engine's global services; the native
  // Rapier body is owned by that subsystem and resolved per call (no stale handle here).
  // Usage from a script: `this.getComponent(Components.RigidBody2D)?.applyForce(0, -10)`.
  // ──────────────────────────────────────────────────────────────────────────

  /** Apply a continuous force this step (mass-dependent). */
  applyForce(fx: number, fy: number): void {
    this.engine?.physics?.applyForce?.(this.entity, fx, fy);
  }

  /** Apply an instantaneous impulse (mass-dependent). */
  applyImpulse(ix: number, iy: number): void {
    this.engine?.physics?.applyImpulse?.(this.entity, ix, iy);
  }

  /** Set linear velocity (units/s). */
  setVelocity(vx: number, vy: number): void {
    this.engine?.physics?.setVelocity?.(this.entity, vx, vy);
  }

  /** Linear velocity (units/s). */
  get velocity(): Vec2Like {
    return this.engine?.physics?.getVelocity?.(this.entity) ?? { x: 0, y: 0 };
  }
  set velocity(v: Vec2Like) {
    this.engine?.physics?.setVelocity?.(this.entity, v.x, v.y);
  }
}
